import { Controller } from "@hotwired/stimulus";

// GO4TEACH lot 24 « Noter les niveaux »: each pupil's level by SEAT NUMBER, fast enough for a
// class of 100 on a phone. Pick a level (the « brush »), tap the seats; a seat tapped again with
// the same level is cleared (an absent pupil has no level). « Le reste à ce niveau » fills every
// seat left. The levels leave the form as hidden `check[levels][seat]` fields.
export default class extends Controller {
  static targets = ["seats", "count", "brushes", "grid", "inputs", "counts"];
  static values = { levels: Object, labels: Object, max: Number };

  connect() {
    this.levels = { ...this.levelsValue };
    this.brush = 1;
    this.render();
  }

  get seatsCount() {
    const value = parseInt(this.seatsTarget.value, 10);
    return Number.isFinite(value) ? Math.min(Math.max(value, 1), this.maxValue) : 0;
  }

  get levelsCount() {
    const checked = this.countTargets.find((input) => input.checked);
    return checked ? parseInt(checked.value, 10) : 3;
  }

  get labels() {
    return this.labelsValue[String(this.levelsCount)] || [];
  }

  render() {
    // Seats beyond the count, or levels beyond the levels' count, are dropped.
    Object.keys(this.levels).forEach((seat) => {
      if (Number(seat) > this.seatsCount || this.levels[seat] > this.levelsCount) delete this.levels[seat];
    });
    if (this.brush > this.levelsCount) this.brush = this.levelsCount;
    this.renderBrushes();
    this.renderGrid();
    this.sync();
  }

  renderBrushes() {
    const buttons = this.labels.map((label, index) => this.brushButton(index + 1, `${index + 1} · ${label}`));
    buttons.push(this.brushButton(0, this.brushesTarget.dataset.clearLabel));
    this.brushesTarget.replaceChildren(...buttons);
  }

  brushButton(level, text) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "ds-btn ds-btn--outline ds-btn--sm g4t-level-brush";
    button.textContent = text;
    button.dataset.level = String(level);
    button.dataset.testid = `teach-level-brush-${level}`;
    button.setAttribute("aria-pressed", String(level === this.brush));
    button.addEventListener("click", () => {
      this.brush = level;
      this.renderBrushes();
    });
    return button;
  }

  renderGrid() {
    this.gridTarget.dataset.levelsCount = String(this.levelsCount);
    const buttons = [];
    for (let seat = 1; seat <= this.seatsCount; seat += 1) {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.seat = String(seat);
      button.dataset.testid = `teach-seat-${seat}`;
      button.addEventListener("click", () => this.tap(seat));
      this.paint(button, seat);
      buttons.push(button);
    }
    this.gridTarget.replaceChildren(...buttons);
  }

  paint(button, seat) {
    const level = this.levels[seat];
    button.dataset.level = level ? String(level) : "";
    button.innerHTML = "";
    button.append(String(seat));
    const small = document.createElement("small");
    small.textContent = level ? String(level) : "·";
    button.append(small);
    button.setAttribute("aria-label", level ? `${seat} · ${this.labels[level - 1]}` : String(seat));
  }

  tap(seat) {
    if (this.brush === 0 || this.levels[seat] === this.brush) delete this.levels[seat];
    else this.levels[seat] = this.brush;
    const button = this.gridTarget.querySelector(`[data-seat="${seat}"]`);
    if (button) this.paint(button, seat);
    this.sync();
  }

  fillRest() {
    if (this.brush === 0) return;
    for (let seat = 1; seat <= this.seatsCount; seat += 1) {
      if (!this.levels[seat]) this.levels[seat] = this.brush;
    }
    this.renderGrid();
    this.sync();
  }

  sync() {
    const inputs = Object.entries(this.levels).map(([seat, level]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = `check[levels][${seat}]`;
      input.value = String(level);
      return input;
    });
    this.inputsTarget.replaceChildren(...inputs);
    const counts = this.labels.map((label, index) => {
      const count = Object.values(this.levels).filter((level) => level === index + 1).length;
      return `${label} ${count}`;
    });
    const none = this.seatsCount - Object.keys(this.levels).length;
    counts.push(`${this.countsTarget.dataset.noneLabel} ${none}`);
    this.countsTarget.textContent = counts.join(" · ");
  }
}
